import React, { useState, useCallback } from 'react';
import { Field } from 'react-final-form';
import { FieldArray } from 'react-final-form-arrays';
import classNames from 'classnames';
import Icons from '../../../../../components/Icons/Icons';
import InfoPopup from '../../../../../components/InfoPopup/InfoPopup';
import { CUSTOM, InputType, TALENT_SERVICES_GUIDES } from '../../../../../util/enums';
import { useIntl } from 'react-intl';
import css from './SignatureServices.module.css';
import GuidePopup from '../../../../../components/GuidePopup/GuidePopup';

const Autographs = ({ values, viewport }) => {
  const [activeIndex, setActiveIndex] = useState(null);
  const isMobileLayout = viewport.width && viewport.width > 0 && viewport.width < 768;
  const intl = useIntl();

  const handleAccordionClick = (e, index) => {
    const interactiveSelector = 'button, input, label, select, textarea';
    if (e.target.closest(interactiveSelector)) {
      if (activeIndex !== index) {
        setActiveIndex(index);
      }
      return;
    }
    setActiveIndex(prevIndex => (prevIndex === index ? null : index));
  };

  const renderField = useCallback(
    (name, type, placeholder) => (
      <div className={css.customFldSec}>
        {type === InputType.NUMBER && <span className={css.currency}>$</span>}
        <Field
          className={css.input}
          name={name}
          component="input"
          type={type}
          placeholder={placeholder}
        />
        {type === InputType.NUMBER && <span className={css.price}>.00</span>}
      </div>
    ),
    []
  );

  const itemConfigs = [
    {
      key: 'photo',
      label: 'Photo',
      tooltip: 'Offer fans an autographed photo, perfect for their collection.',
    },
    {
      key: 'tradingCard',
      label: 'Trading Card',
      tooltip: 'Create exclusive autographed trading cards for fans to add to their memorabilia.',
    },
    {
      key: 'poster',
      label: 'Poster',
      tooltip: 'Provide fans with your signature on a poster, a must-have for true fans.',
    },
    {
      key: 'showFanProvidedItems',
      label: 'Fan or Brand Provided Item',
      tooltip: 'Sign and personalize items that fans send in.',
    },
    {
      key: 'custom',
      label: 'Custom Talent Provided',
      tooltip: 'Provide a unique signed item that you personally select for your fans.',
      suggestions: [
        { title: 'Jersey', description: 'Offer a signed jersey perfect for collectors.' },
        {
          title: 'Equipment',
          description: 'Autograph a piece of sports equipment, such as a ball, cleats, or gloves.',
        },
        {
          title: 'Limited-Edition Memorabilia',
          description: 'Offer a rare, signed item exclusive to this marketplace.',
        },
      ],
    },
  ];

  const faqsData = [
    {
      question: 'Autographs',
      answer: (
        <div className={css.talentSec}>
          {itemConfigs.map(({ key, label, tooltip, suggestions }) => (
            <div key={key} className={css.formFld}>
              <label className={css.talentOptions}>
                <Field
                  name={`autographs.typeOfItems.${key}`}
                  component="input"
                  type="checkbox"
                  format={v => Boolean(v)}
                  parse={v => (v ? true : undefined)}
                />
                <span>{label}</span>
                <InfoPopup triggerOn="hover" info={tooltip} />
              </label>

              {/* Non-custom items: show single price input */}
              {values?.autographs?.typeOfItems?.[key] && key !== CUSTOM && (
                <>
                  {renderField(`autographs.${key}Price`, 'number', `Enter Flat Price for ${label}`)}
                </>
              )}

              {/* For "custom", display FieldArray with at least one custom field by default */}

              {/* Suggestions for custom items */}
              {/* Suggestions for custom items - only show when checkbox is selected */}
              {values?.autographs?.typeOfItems?.[key] && key === CUSTOM && suggestions && (
                <div className={css.suggestionBox}>
                  <p className={css.suggestionTitle}>Suggestions:</p>
                  <ul className={css.suggestionList}>
                    {suggestions.map((item, idx) => (
                      <li key={idx}>
                        <strong>{item.title}:</strong> {item.description}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {values?.autographs?.typeOfItems?.[key] && key === CUSTOM && (
                <>
                  <FieldArray name="autographs.customItems">
                    {({ fields }) => {
                      if (fields.length === 0) {
                        fields.push({ title: '', price: '' });
                      }

                      return (
                        <div className={css.customItemContainer}>
                          {fields.map((name, index) => (
                            <div key={name} className={css.customItemRow}>
                              <div className={css.customItemField}>
                                <Field
                                  name={`${name}.title`}
                                  component="input"
                                  type="text"
                                  placeholder="Enter title"
                                  className={css.input}
                                />
                              </div>
                              <div className={css.customItemField}>
                                <div className={css.customFldSec}>
                                  <span className={css.currency}>$</span>
                                  <Field
                                    name={`${name}.price`}
                                    component="input"
                                    type="number"
                                    placeholder="Enter price"
                                    className={css.input}
                                  />
                                  <span className={css.price}>.00</span>
                                </div>
                              </div>
                              <button
                                type="button"
                                className={css.removeBtn}
                                onClick={() => fields.remove(index)}
                              >
                                {isMobileLayout ? (
                                  <>
                                    <Icons name="bin" /> Delete
                                  </>
                                ) : (
                                  <Icons name="bin" />
                                )}
                              </button>
                            </div>
                          ))}
                          <button
                            className={css.addCustomBtn}
                            type="button"
                            onClick={() => fields.push({ title: '', price: '' })}
                          >
                            {intl.formatMessage({
                              id: 'Autographs.addAnotherCustomItem',
                            })}
                          </button>
                        </div>
                      );
                    }}
                  </FieldArray>
                </>
              )}
            </div>
          ))}
        </div>
      ),
    },
  ];

  return (
    <>
      {faqsData.map((faq, index) => (
        <div
          key={index}
          className={classNames(css.faqCard, {
            [css.activeFAQ]: activeIndex === index,
          })}
          onClick={e => handleAccordionClick(e, index)}
        >
          <div className={css.faqBody}>
            <span className={css.faqHeading}>
              <div>
                {faq?.question} <GuidePopup externalLink={TALENT_SERVICES_GUIDES.AUTOGRAPHS} />
              </div>
              <span className={css.faqIcon}>
                {activeIndex === index ? <Icons name="upAngle" /> : <Icons name="downAngle" />}
              </span>
            </span>
            {activeIndex === index && (
              <>
                <p className={css.tooltip}>{intl.formatMessage({ id: 'Autographs.tooltip' })}</p>
                <div className={css.faqDescription}>{faq?.answer}</div>
              </>
            )}
          </div>
        </div>
      ))}
    </>
  );
};

export default Autographs;
